import { Box, Group, Paper, SimpleGrid, Stack, Text, Button, Flex } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "@/context/UserContext";
import AppHeaderUserActions from "@/components/shared/AppHeaderUserActions";
import DashboardCard from "./DashboardCard";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import CollaborationTimelineCard, {
  CollaborationTimelineCardSkeleton,
  type CollaborationTimelineCardItem,
} from "./CollaborationTimelineCard";
import TitleComponent from "@/components/shared/TitleComponent";
import { useVerificationModal } from "@/components/layout/AppLayout";

export default function DashboardPage() {
  const isMobileHeader = useMediaQuery("(max-width: 47.99em)");
  const navigate = useNavigate();
  const { userDetails, pendingRequestCounts, collaborationSidebarCounts, shortlistedIds } = useUserContext();
  const { openVerificationModal } = useVerificationModal();
  const userName = userDetails?.name?.trim() || "User";
  const [timelineItems, setTimelineItems] = useState<CollaborationTimelineCardItem[]>([]);
  const [loadingTimeline, setLoadingTimeline] = useState(true);
  const companyStatus = userDetails?.company?.status;
  const canShowVerifyButton = companyStatus !== "VERIFIED" && companyStatus !== "SYSTEM_VERIFIED";

  useEffect(() => {
    let mounted = true;

    const fetchTimeline = async () => {
      setLoadingTimeline(true);
      try {
        const data = await trpc.collaboration.getDashboardTimeline.query();
        if (!mounted) return;
        setTimelineItems((data || []) as CollaborationTimelineCardItem[]);
      } catch (err) {
        handleTrpcError(err);
        if (!mounted) return;
        setTimelineItems([]);
      } finally {
        if (mounted) setLoadingTimeline(false);
      }
    };

    if (userDetails?.company?.id) {
      fetchTimeline();
    } else {
      setLoadingTimeline(false);
      setTimelineItems([]);
    }

    return () => {
      mounted = false;
    };
  }, [userDetails?.company?.id]);

  /** Fit AppShell main; small reserve for mobile header / status banner. */
  const pageHeight = isMobileHeader ? "calc(100dvh - 72px)" : "calc(100dvh - 32px)";

  return (
    <Box
      w="100%"
      maw="100%"
      style={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        height: pageHeight,
        maxHeight: pageHeight,
        overflow: "hidden",
      }}
    >
      <Box style={{ flexShrink: 0 }}>
        <TitleComponent
          title={`Welcome! ${userName}`}
          description="Here is your dashboard overview."
          rightSection={<AppHeaderUserActions />}
        />
      </Box>

      <Box
        p="lg"
        pt={0}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack gap="md" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          {canShowVerifyButton && (
            <Paper p="md" radius="md" withBorder style={{ flexShrink: 0 }}>
              <Flex justify="space-between" align="center" gap="sm" wrap="wrap">
                <Box>
                  <Text fw={700}>Complete Company Verification</Text>
                  <Text size="sm" c="dimmed">
                    Verify your company details from dashboard. Credits are deducted only when verification is processed.
                  </Text>
                </Box>
                <Button onClick={openVerificationModal}>Verify Company</Button>
              </Flex>
            </Paper>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="md" style={{ flexShrink: 0 }}>
            <Box>
              <DashboardCard
                title="Shortlist"
                count={shortlistedIds.size}
                description="Companies you have shortlisted"
                onClick={() => navigate("/shortlist")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Requests Sent"
                count={pendingRequestCounts.sent}
                description="Requests you've sent waiting for response"
                onClick={() => navigate("/requests")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Requests Received"
                count={pendingRequestCounts.received}
                description="Requests you've received from others"
                onClick={() => navigate("/requests")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Active Collaborations"
                count={collaborationSidebarCounts.active}
                description="Ongoing collaborations with partners"
                onClick={() => navigate("/collaborations")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Closed Collaborations"
                count={collaborationSidebarCounts.closed}
                description="Completed or inactive collaborations"
                onClick={() => navigate("/collaborations")}
              />
            </Box>
          </SimpleGrid>

          <Paper
            p="lg"
            radius="md"
            withBorder
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Group justify="space-between" align="center" mb="sm" style={{ flexShrink: 0 }}>
              <Box>
                <Text fw={700} fz="lg">
                  Collaboration Timeline
                </Text>
                <Text c="dimmed" size="sm">
                  Track the progress of your collaboration steps from both sides.
                </Text>
              </Box>
              <Group gap="lg">
                <Group gap={6}>
                  <Box w={10} h={10} style={{ borderRadius: "50%", backgroundColor: "var(--mantine-color-green-6)" }} />
                  <Text size="sm" c="dimmed">
                    Completed
                  </Text>
                </Group>
                <Group gap={6}>
                  <Box
                    w={10}
                    h={10}
                    style={{ borderRadius: "50%", backgroundColor: "var(--mantine-color-gray-5)" }}
                  />
                  <Text size="sm" c="dimmed">
                    Pending
                  </Text>
                </Group>
              </Group>
            </Group>

            <Box
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                paddingRight: 4,
              }}
            >
              {loadingTimeline ? (
                <Stack gap="sm">
                  <CollaborationTimelineCardSkeleton />
                </Stack>
              ) : timelineItems.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No collaborations found.
                </Text>
              ) : (
                <Stack gap="sm">
                  {timelineItems.map((item) => (
                    <CollaborationTimelineCard key={item.matchId} item={item} />
                  ))}
                </Stack>
              )}
            </Box>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}
